import { useEffect, useMemo, useState } from 'react';
import { useResolveResolutionDependentOption } from '@lingoda/student-frontend/router';
import { AppBarGap, LightFooter } from '@lingoda/student-navigation';
import { HeaderContextInitializer } from '@lingoda/contexts';
import { Box, dvh } from '@lingoda/ui';
import { MIN_HEIGHT_HEADER } from '@lingoda/ui/theme/common';
import { getQueryParam } from '@lingoda/urls';
import { resolveDynamicOption, useLocation } from '@lingoda/router';
import type { RouteConfigOption } from '@lingoda/router';
import { showToast } from '@lingoda/toasts';
import { useElementSize } from '@lingoda/hooks';
import { translateErrorCode } from '@lingoda/api';
import { ContentWrapper } from './ContentWrapper';
import { PublicPageHeader } from './PublicPageHeader';
import type { PropsWithChildren } from 'react';

export const PublicPageFrame = (props: PropsWithChildren<RouteConfigOption>) => {
    const {
        logoIsIcon = false,
        disableLogoLink = false,
        hideNavigation = false,
        headerIsPortal = false,
        navigationElevation = 0,
    } = props;
    const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>();
    const hideHeader = useResolveResolutionDependentOption(props.hideHeader);
    const hideFooter = resolveDynamicOption(props.hideFooter);
    const contentBg = resolveDynamicOption(props.contentBg);
    const headerHeight = useElementSize(headerRef, 'offsetHeight', {
        defaultValue: hideHeader ? 0 : MIN_HEIGHT_HEADER,
    });
    const location = useLocation();
    const error = getQueryParam<string>('error', location);
    const errorType = getQueryParam<string>('errorType', location);

    useEffect(() => {
        const errorMessage = (errorType && translateErrorCode(errorType)) || error;

        if (errorMessage) {
            showToast('error', errorMessage);
        }
    }, [error, errorType]);

    const headerRefs = useMemo(
        () => ({
            navHeader: setHeaderRef,
        }),
        [],
    );

    return (
        <HeaderContextInitializer height={headerHeight} headerTheme={props.theme || 'dark'}>
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                minHeight={[dvh('100dvh')]}
                bgcolor={props.backgroundColor}
            >
                {!hideHeader && (
                    <PublicPageHeader
                        logoIsIcon={logoIsIcon}
                        disableLogoLink={disableLogoLink}
                        hideNavigation={hideNavigation}
                        headerIsPortal={headerIsPortal}
                        navigationElevation={navigationElevation}
                        refs={headerRefs}
                    />
                )}
                <ContentWrapper bg={contentBg}>
                    <AppBarGap />
                    {props.children}
                </ContentWrapper>
                {!hideFooter && <LightFooter />}
            </Box>
        </HeaderContextInitializer>
    );
};
